import { useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  capitalize,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import { useEffect } from "react";
import { CustomTextView } from "../../../component/CustomTextView";
import { HeaderView } from "../../../component/HeaderView";
import { ROUTE_NEW } from "../../../../data/constant/RouteConstants";
import { CustomerDetailsViewModel } from "./CustomerDetailsViewModel";
import {
  provideDownloadMultipleOrderReceiptsUseCase,
  provideGetCategoryUseCase,
  provideGetCustomerByIdUseCase,
  provideGetCustomerOrdersUseCase,
  provideUpdateOrderUseCase,
} from "../../../di/UseCasesModule";
import Marquee from "react-fast-marquee";
import { LoadingView } from "../../../component/LoadingView";
import { CustomCardView } from "../../../component/CustomCardView";
import moment from "moment";
import { EmptyView } from "../../../component/EmptyView";
import { OrdersTableView } from "../../orders/OrdersView";
import { Customer, getFullAddress } from "../../../../domain/model/Customer";
import {
  DownloadRounded,
  HomeRounded,
  NumbersRounded,
  Person4Rounded,
  PhoneRounded,
} from "@mui/icons-material";
import { MAIN_FONT_FAMILY } from "../../../../data/constant/Constants";

export function CustomerDetailsView() {
  const { id } = useParams();
  const viewModel = CustomerDetailsViewModel(
    provideGetCustomerByIdUseCase(),
    provideGetCustomerOrdersUseCase(),
    provideUpdateOrderUseCase(),
    provideDownloadMultipleOrderReceiptsUseCase()
  );

  useEffect(() => {
    if (id) {
      viewModel.fetchCustomer(id);
      viewModel.fetchOrders(id);
    }
  }, []);

  const customer = viewModel.customer;

  return (
    <Box>
      <Stack>
        {viewModel.isLoading ? (
          <LoadingView />
        ) : (
          <>
            <Stack>
              <HeaderView
                title={"Customer"}
                alignItems="start"
                titleLayout={
                  <>
                    {customer?._id && (
                      <Marquee style={{ width: 100 }} delay={2} pauseOnHover>
                        <CustomTextView
                          text={`#${customer._id}`}
                          fontWeight="300"
                          variant="h6"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                      </Marquee>
                    )}
                  </>
                }
                layout={<></>}
              />

              {customer && (
                <Grid container pl={3} pr={3} pb={3} pt={0} spacing={3}>
                  <CustomerInfoView customer={customer} />
                  <Grid size={12}>
                    <Stack spacing={2}>
                      <Stack
                        spacing={1}
                        direction={"row"}
                        width={"100%"}
                        justifyContent={"space-between"}
                      >
                        <CustomTextView
                          text="Orders"
                          fontWeight="bold"
                          variant="h5"
                        />
                        <Button
                          onClick={viewModel.downloadMultipleOrderReceipts}
                          variant="outlined"
                          sx={{
                            borderRadius: 22,
                            borderColor: COLOR_BLACK,
                            "&:hover": {
                              borderColor: COLOR_BLACK,
                            },
                          }}
                        >
                          <DownloadRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                          <CustomTextView
                            text={`Download Receipts (${
                              viewModel.orders?.filter((o) => o.selected).length
                            })`}
                            fontWeight="bold"
                            fontSize={16}
                            props={{ textTransform: "none" }}
                            textColor={COLOR_BLACK}
                          />
                        </Button>
                      </Stack>
                      {viewModel.ordersLoading ? (
                        <LoadingView minHeight="30vh" />
                      ) : (
                        <>
                          {viewModel.orders.length > 0 ? (
                            <OrdersTableView
                              orders={viewModel.orders}
                              hasCustomer={false}
                              onStatusChanged={(id, status) => {
                                viewModel.updateOrder(id, { status });
                              }}
                              selectUnselectOrder={
                                viewModel.selectUnselectOrder
                              }
                            />
                          ) : (
                            <EmptyView minHeight="30vh" />
                          )}
                        </>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}

export function CustomerInfoView({ customer }: { customer: Customer }) {
  return (
    <>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 5 }}>
        <CustomCardView
          padding={2}
          children={
            <Stack spacing={2} alignItems={"center"}>
              <Avatar
                src={customer.profileImage}
                sx={{ width: 140, height: 140 }}
              />
              <Stack alignItems={"center"}>
                <CustomTextView
                  text={customer?.name}
                  fontWeight={"bold"}
                  variant={"h5"}
                  textAlign="center"
                />
                <CustomTextView
                  text={customer.user?.email}
                  variant="h6"
                  textColor={COLOR_LESS_BLACK}
                  props={{
                    flexWrap: "wrap",
                    wordWrap: "break-word",
                  }}
                  textAlign="center"
                />
              </Stack>
              <CustomTextView
                text={`joined ${moment(customer.user?.createdAt).fromNow()}`}
                variant="body1"
                textColor={COLOR_LIGHT_GRAY}
                props={{ pt: 1 }}
                textAlign="center"
              />
            </Stack>
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 7 }}>
        <CustomCardView
          padding={2}
          children={
            <Stack spacing={2}>
              {customer.age && (
                <Stack
                  alignItems={"center"}
                  direction={"row"}
                  width={"100%"}
                  justifyContent={"space-between"}
                >
                  <Stack alignItems={"center"} direction={"row"} spacing={0.5}>
                    <NumbersRounded
                      sx={{ color: COLOR_LIGHT_GRAY }}
                      fontSize="small"
                    />
                    <CustomTextView
                      text={"Age"}
                      variant="body2"
                      textColor={COLOR_LIGHT_GRAY}
                      props={{
                        flexWrap: "wrap",
                        wordWrap: "break-word",
                      }}
                    />
                  </Stack>
                  <CustomTextView
                    text={`${customer?.age}`}
                    fontWeight={"bold"}
                    variant={"h5"}
                    textAlign="end"
                  />
                </Stack>
              )}
              {customer.gender && (
                <Stack
                  alignItems={"center"}
                  direction={"row"}
                  width={"100%"}
                  justifyContent={"space-between"}
                >
                  <Stack alignItems={"center"} direction={"row"} spacing={0.5}>
                    <Person4Rounded
                      sx={{ color: COLOR_LIGHT_GRAY }}
                      fontSize="small"
                    />
                    <CustomTextView
                      text={"Gender"}
                      variant="body2"
                      textColor={COLOR_LIGHT_GRAY}
                      props={{
                        flexWrap: "wrap",
                        wordWrap: "break-word",
                      }}
                    />
                  </Stack>
                  <CustomTextView
                    text={`${capitalize(customer.gender)}`}
                    fontWeight={"bold"}
                    variant={"h5"}
                    textAlign="end"
                  />
                </Stack>
              )}
              <Stack
                alignItems={"center"}
                direction={"row"}
                width={"100%"}
                justifyContent={"space-between"}
              >
                <Stack alignItems={"center"} direction={"row"} spacing={0.5}>
                  <PhoneRounded
                    sx={{ color: COLOR_LIGHT_GRAY }}
                    fontSize="small"
                  />
                  <CustomTextView
                    text={"Phone Number"}
                    variant="body2"
                    textColor={COLOR_LIGHT_GRAY}
                    props={{
                      flexWrap: "wrap",
                      wordWrap: "break-word",
                    }}
                  />
                </Stack>
                <CustomTextView
                  text={`${customer?.phoneNumber?.countryCode}${customer?.phoneNumber?.number}`}
                  fontWeight={"bold"}
                  variant={"h5"}
                  textAlign="end"
                />
              </Stack>
              {customer.homeAddress && (
                <Stack
                  alignItems={"center"}
                  direction={"row"}
                  width={"100%"}
                  justifyContent={"space-between"}
                >
                  <Stack alignItems={"center"} direction={"row"} spacing={0.5}>
                    <HomeRounded
                      sx={{ color: COLOR_LIGHT_GRAY }}
                      fontSize="small"
                    />
                    <CustomTextView
                      text={"Home Address"}
                      variant="body2"
                      textColor={COLOR_LIGHT_GRAY}
                      props={{
                        flexWrap: "wrap",
                        wordWrap: "break-word",
                      }}
                    />
                  </Stack>
                  <AddressText
                    fullAddress={getFullAddress(customer.homeAddress)}
                  />
                </Stack>
              )}
            </Stack>
          }
        />
      </Grid>
    </>
  );
}

const tooltips: Record<number, string> = {
  0: "Country",
  1: "Zone Number",
  2: "Street Number",
  3: "Building Number",
  4: "Floor Number",
};

export function AddressText({ fullAddress }: { fullAddress: string }) {
  const parts = fullAddress.split(",").map((p) => p.trim());

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap">
      {parts.map((part, idx) => (
        <Tooltip key={idx} title={tooltips[idx] || ""}>
          <Typography
            component="span"
            sx={{
              cursor: tooltips[idx] ? "help" : "default",
              fontFamily: MAIN_FONT_FAMILY,
              color: COLOR_BLACK,
              fontWeight: "bold",
            }}
            variant="h5"
          >
            {part}
            {idx < parts.length - 1 && ","}
          </Typography>
        </Tooltip>
      ))}
    </Stack>
  );
}
